/**
 * Claude subscription-key usage sampler.
 *
 * Reports the session/weekly rate-limit utilization of the key the pod runs
 * under. The numbers come from the Claude CLI's own `/usage` command
 * (`claude -p "/usage"`, plain-text, client-side) — the authoritative,
 * account-wide gauges the CLI itself shows. Mapped into the `rate_limit_update`
 * events the API already persists (`persistRateLimitSnapshot` →
 * ClaudeSubscriptionKey.sessionUsage/weeklyUsage), which keeps User Settings,
 * the usage widget, and `selectBestKey` rotation honest.
 *
 * Best-effort: any probe/parse failure yields `[]`, never throws.
 */
import { createServiceLogger } from "../utils/logger.js";
import { parseUsageGauges } from "../usage/parse-usage.js";
import { runUsageProbe } from "../usage/run-probe.js";

const logger = createServiceLogger("usage-sampler");

export interface KeyUsageSample {
  rateLimitType: string;
  /** 0..1 utilization fraction (matches UsageBar / getRateLimitColor). */
  utilization: number;
  status: string;
}

/**
 * Sample the current key's usage via `claude -p "/usage"` and map it to
 * rate-limit samples. `token` presence gates sampling: an API-key pod (no OAuth
 * subscription token) has no `/usage` gauges, so it no-ops. `probe` is
 * injectable for tests; production callers pass only `token`. Best-effort —
 * never throws.
 */
export async function sampleKeyUsage(
  token: string | undefined,
  probe: () => Promise<string> = runUsageProbe,
): Promise<KeyUsageSample[]> {
  if (!token) return [];
  try {
    const stdout = await probe();
    const { sessionUsage, weeklyUsage } = parseUsageGauges(stdout);
    const samples: KeyUsageSample[] = [];
    if (sessionUsage !== null) {
      samples.push({ rateLimitType: "five_hour", utilization: sessionUsage, status: "allowed" });
    }
    if (weeklyUsage !== null) {
      samples.push({ rateLimitType: "seven_day", utilization: weeklyUsage, status: "allowed" });
    }
    if (samples.length === 0) {
      logger.info("usage sample produced no gauges", { stdoutLength: stdout.length });
    }
    return samples;
  } catch (error) {
    logger.info("usage sample failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}
